import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { Icon } from '@/components/core/Icon';
import { DiscardChangesConfirmationModal } from '@/components/DiscardChangesConfirmationModal';
import { useTranslation } from '@/hooks/useTranslation';
import { PersistedItem } from '@/types/DataModel';

const ItemsPaginationFooter = ({
    style,
    isItemEditorDialog,
    currentItemIndex,
    onlineCatalogState,
    onNavigateToItem,
    hasUnsavedChanges = false,
}: {
    style: string,
    isItemEditorDialog: boolean;
    currentItemIndex: number;
    onlineCatalogState: any;
    onNavigateToItem?: (targetItem: PersistedItem, targetItemIndex: number) => void;
    hasUnsavedChanges?: boolean;
}) => {
    const { t } = useTranslation();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<{
        direction: 'previous' | 'next';
        event: any;
    } | null>(null);

    const items = isItemEditorDialog ? onlineCatalogState : onlineCatalogState.items;

    const performNavigation = (direction: 'previous' | 'next', e: any) => {
        e.preventDefault();
        let targetItemIndex = currentItemIndex;

        if (direction === 'previous' && currentItemIndex !== null && currentItemIndex > 0) {
            targetItemIndex = currentItemIndex - 1;
        } else if (direction === 'next' && currentItemIndex < items.length - 1) {
            targetItemIndex = currentItemIndex + 1;
        }

        if (targetItemIndex === currentItemIndex) return;

        const targetItem = isItemEditorDialog
            ? onlineCatalogState[targetItemIndex]
            : onlineCatalogState.items[targetItemIndex];

        if (onNavigateToItem) {
            onNavigateToItem(targetItem, targetItemIndex);
        } else {
            if (isItemEditorDialog) {
                sessionStorage.setItem('items', JSON.stringify(onlineCatalogState));
                const targetUrl = `/edit/${targetItem.primaryKey__}/attribute/gtin`;
                window.location.href = targetUrl;
            } else {
                sessionStorage.setItem('onlineCatalogState', JSON.stringify(onlineCatalogState));
                const urlParams = new URLSearchParams(window.location.search);
                const withPublicationTaskChecksums = urlParams.has('withPublicationTaskChecksums');
                const targetUrl = `/items/detail/${targetItem.primaryKey__}`;
                const fullUrl = withPublicationTaskChecksums
                    ? `${targetUrl}?checksum=${targetItem.checksum__}`
                    : targetUrl;

                window.location.href = fullUrl;
            }
        }
    };

    const navigateToItem = (direction: 'previous' | 'next', e: any) => {
        if (isItemEditorDialog && hasUnsavedChanges) {
            setPendingNavigation({ direction, event: e });
            setShowConfirmationModal(true);
        } else {
            performNavigation(direction, e);
        }
    };

    const handleDiscardChanges = () => {
        setShowConfirmationModal(false);
        if (pendingNavigation) {
            performNavigation(pendingNavigation.direction, pendingNavigation.event);
        }
        setPendingNavigation(null);
    };

    const handleCloseModal = () => {
        setShowConfirmationModal(false);
        setPendingNavigation(null);
    };

    return (
        <>
            <div className={style}>
                {currentItemIndex != 0 && <a
                    onClick={(e)=>navigateToItem('previous',e)}
                    style={{textDecoration:'none'}}
                    className={`${currentItemIndex === 0 ? 'tw-text-gray-500 pointer-events-none' : 'tw-text-black-500'} tw-flex tw-items-center`}
                >
                    <Icon size='lg' icon={ChevronLeftIcon}/>
                    <span className="tw-text-center tw-px-1">
                        {t('ITEM_EDITOR.PREVIOUS_ITEM')}
                    </span>
                </a>}
                <span className="tw-text-center tw-flex tw-items-center tw-px-1">
                    {currentItemIndex + 1} | {items.length}
                </span>

                {currentItemIndex != items.length - 1 &&  <a
                    onClick={(e)=>navigateToItem('next',e)}
                    style={{textDecoration:'none'}}
                    className={`${currentItemIndex === items.length - 1 ? 'tw-text-gray-500 pointer-events-none' : 'tw-text-black-500'} tw-flex tw-items-center`}
                    data-testid="next-item"
                >
                    <span className="tw-text-center tw-px-1">
                        {t('ITEM_EDITOR.NEXT_ITEM')}
                    </span>
                    <Icon icon={ChevronRightIcon}size='lg'/>
                </a>}
            </div>

            <DiscardChangesConfirmationModal
                show={showConfirmationModal}
                onDiscard={handleDiscardChanges}
                onClose={handleCloseModal}
            />
        </>
    );
};

export default ItemsPaginationFooter;
